// src/components/vendor/VendorOnboarding.tsx
import React, { useState } from 'react';
import {
  Box, Container, Stepper, Step, StepLabel, Button, Paper,
  TextField, CircularProgress, Alert, Typography, Checkbox,
  FormControlLabel, Select, MenuItem, InputLabel, FormControl,
  Divider, FormGroup, FormHelperText,
  Chip,
  IconButton
} from '@mui/material';
import { Delete, CloudUpload, Add } from '@mui/icons-material';

interface BusinessFormData {
  businessName: string;
  businessDescription: string;
  registrationNumber: string;
  taxNumber: string;
  vatNumber: string;
  vendorTypeId: string;
  businessCategory: string;
  directors: Array<{
    firstName: string;
    lastName: string;
    identification: string;
    identificationType: string;
    sex: string;
    email: string;
    telephone: string;
    country: string;
  }>;
  contactPerson: {
    firstName: string;
    lastName: string;
    identification: string;
    identificationType: string;
    sex: string;
    email: string;
    telephone: string;
    country: string;
  };
  communication: {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    city: string;
    region: string;
    postalCode: string;
    country: string;
    telephone: string;
  };
  bankingDetails: {
    bankName: string;
    accountNumber: string;
    branchCode: string;
    accountType: string;
    accountHolderName: string;
  };
  deliveryAreas: string[];
  outlets: Array<{
    outletName: string;
    outletDescription: string;
    outletTypeId: string;
    contactPerson: {
      firstName: string;
      lastName: string;
      identification: string;
      identificationType: string;
      sex: string;
      email: string;
      telephone: string;
      country: string;
    };
    communication: {
      addressLine1: string;
      addressLine2: string;
      addressLine3: string;
      city: string;
      region: string;
      postalCode: string;
      country: string;
      telephone: string;
      faxNumber: string;
    };
    operatingHours: {
      monday: { open: string; close: string; closed: boolean };
      tuesday: { open: string; close: string; closed: boolean };
      wednesday: { open: string; close: string; closed: boolean };
      thursday: { open: string; close: string; closed: boolean };
      friday: { open: string; close: string; closed: boolean };
      saturday: { open: string; close: string; closed: boolean };
      sunday: { open: string; close: string; closed: boolean };
    };
  }>;
  documents: {
    registrationDoc: File | null;
    identificationDoc: File | null;
    otherDocs: File[];
  };
  termsAgreed: boolean;
}

const VendorOnboarding: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<BusinessFormData>({
    businessName: '',
    businessDescription: '',
    registrationNumber: '',
    taxNumber: '',
    vatNumber: '',
    vendorTypeId: 'VENDOR-PVT-LTD',
    businessCategory: '',
    directors: [],
    contactPerson: {
      firstName: '',
      lastName: '',
      identification: '',
      identificationType: 'IDENTITY CARD',
      sex: 'MALE',
      email: '',
      telephone: '',
      country: 'ZIMBABWE'
    },
    communication: {
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      city: '',
      region: '',
      postalCode: '',
      country: 'ZIMBABWE',
      telephone: ''
    },
    bankingDetails: {
      bankName: '',
      accountNumber: '',
      branchCode: '',
      accountType: 'CURRENT',
      accountHolderName: ''
    },
    deliveryAreas: ['Harare'],
    outlets: [],
    documents: {
      registrationDoc: null,
      identificationDoc: null,
      otherDocs: []
    },
    termsAgreed: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newDeliveryArea, setNewDeliveryArea] = useState('');

  const validateStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (activeStep === 0) {
      if (!formData.businessName.trim()) {
        newErrors.businessName = 'Business name is required';
      }
      if (!formData.businessCategory.trim()) {
        newErrors.businessCategory = 'Business category is required';
      }
    }

    if (activeStep === 2) {
      if (!formData.contactPerson.firstName.trim()) {
        newErrors.contactPersonFirstName = 'First name is required';
      }
      if (!formData.contactPerson.email.trim()) {
        newErrors.contactPersonEmail = 'Email is required';
      }
      if (!formData.communication.addressLine1.trim()) {
        newErrors.addressLine1 = 'Address is required';
      }
    }

    if (activeStep === 4) {
      if (!formData.bankingDetails.bankName.trim()) {
        newErrors.bankName = 'Bank name is required';
      }
      if (!formData.bankingDetails.accountNumber.trim()) {
        newErrors.accountNumber = 'Account number is required';
      }
    }

    if (activeStep === 5) {
      if (!formData.termsAgreed) {
        newErrors.termsAgreed = 'You must agree to the terms and conditions';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setActiveStep(prev => prev < 6 ? prev + 1 : prev);
  };

  const handleBack = () => setActiveStep(prev => prev > 0 ? prev - 1 : prev);

  const handleFormChange = (path: string, value: any) => {
    setFormData(prev => {
      const paths = path
        .replace(/\[/g, '.')
        .replace(/\]/g, '')
        .split('.')
        .filter(p => p !== "");

      const newData = JSON.parse(JSON.stringify(prev));
      let current: any = newData;
      
      for (let i = 0; i < paths.length - 1; i++) {
        const key = paths[i];
        const nextKey = paths[i + 1];
        
        if (!current[key]) {
          current[key] = isNaN(Number(nextKey)) ? {} : [];
        }
        
        if (Array.isArray(current[key]) && !/^\d+$/.test(nextKey)) {
          current[key] = { ...current[key] };
        }
        
        current = current[key];
      }

      const lastKey = paths[paths.length - 1];
      if (Array.isArray(current)) {
        current[Number(lastKey)] = value;
      } else {
        current[lastKey] = value;
      }
      
      return newData;
    });
  };

  const handleFileUpload = (field: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    if (field === 'registrationDoc' || field === 'identificationDoc') {
      handleFormChange(`documents.${field}`, files[0]);
    } else if (field === 'otherDocs') {
      const newFiles = Array.from(files);
      setFormData(prev => ({
        ...prev,
        documents: {
          ...prev.documents,
          otherDocs: [...prev.documents.otherDocs, ...newFiles]
        }
      }));
    }
  };

  const removeFile = (field: string, index?: number) => {
    if (field === 'registrationDoc' || field === 'identificationDoc') {
      handleFormChange(`documents.${field}`, null);
    } else if (field === 'otherDocs' && index !== undefined) {
      setFormData(prev => ({
        ...prev,
        documents: {
          ...prev.documents,
          otherDocs: prev.documents.otherDocs.filter((_, i) => i !== index)
        }
      }));
    }
  };

  const addDirector = () => {
    setFormData(prev => ({
      ...prev,
      directors: [...prev.directors, {
        firstName: '',
        lastName: '',
        identification: '',
        identificationType: 'IDENTITY CARD',
        sex: 'MALE',
        email: '',
        telephone: '',
        country: 'ZIMBABWE'
      }]
    }));
  };

  const removeDirector = (index: number) => {
    setFormData(prev => ({
      ...prev,
      directors: prev.directors.filter((_, i) => i !== index)
    }));
  };

  const addOutlet = () => {
    setFormData(prev => ({
      ...prev,
      outlets: [...prev.outlets, {
        outletName: '',
        outletDescription: '',
        outletTypeId: 'RETAIL',
        contactPerson: {
          firstName: '',
          lastName: '',
          identification: '',
          identificationType: 'IDENTITY CARD',
          sex: 'MALE',
          email: '',
          telephone: '',
          country: 'ZIMBABWE'
        },
        communication: {
          addressLine1: '',
          addressLine2: '',
          addressLine3: '',
          city: '',
          region: '',
          postalCode: '',
          country: 'ZIMBABWE',
          telephone: '',
          faxNumber: ''
        },
        operatingHours: {
          monday: { open: '08:00', close: '17:00', closed: false },
          tuesday: { open: '08:00', close: '17:00', closed: false },
          wednesday: { open: '08:00', close: '17:00', closed: false },
          thursday: { open: '08:00', close: '17:00', closed: false },
          friday: { open: '08:00', close: '17:00', closed: false },
          saturday: { open: '08:00', close: '17:00', closed: false },
          sunday: { open: '08:00', close: '17:00', closed: true }
        }
      }]
    }));
  };

  const removeOutlet = (index: number) => {
    setFormData(prev => ({
      ...prev,
      outlets: prev.outlets.filter((_, i) => i !== index)
    }));
  };

  const addDeliveryArea = () => {
    if (newDeliveryArea.trim() !== '') {
      setFormData(prev => ({
        ...prev,
        deliveryAreas: [...prev.deliveryAreas, newDeliveryArea.trim()]
      }));
      setNewDeliveryArea('');
    }
  };

  const removeDeliveryArea = (index: number) => {
    setFormData(prev => ({
      ...prev,
      deliveryAreas: prev.deliveryAreas.filter((_, i) => i !== index)
    }));
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" gutterBottom>Business Information</Typography>
            
            <TextField
              label="Business Name *"
              value={formData.businessName}
              onChange={(e) => handleFormChange('businessName', e.target.value)}
              error={!!errors.businessName}
              helperText={errors.businessName}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Business Description"
              value={formData.businessDescription}
              onChange={(e) => handleFormChange('businessDescription', e.target.value)}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
            />
            <TextField
              label="Registration Number"
              value={formData.registrationNumber}
              onChange={(e) => handleFormChange('registrationNumber', e.target.value)}
              fullWidth
              variant="outlined"
            />

            <FormControl fullWidth variant="outlined">
              <InputLabel>Business Type</InputLabel>
              <Select
                value={formData.vendorTypeId}
                onChange={(e) => handleFormChange('vendorTypeId', e.target.value)}
                label="Business Type"
              >
                <MenuItem value="VENDOR-PVT-LTD">Private Limited Company</MenuItem>
                <MenuItem value="VENDOR-SOLE-PROP">Sole Proprietorship</MenuItem>
                <MenuItem value="VENDOR-PARTNERSHIP">Partnership</MenuItem>
                <MenuItem value="VENDOR-PUBLIC-LTD">Public Limited Company</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth variant="outlined" error={!!errors.businessCategory}>
              <InputLabel>Business Category *</InputLabel>
              <Select
                value={formData.businessCategory}
                onChange={(e) => handleFormChange('businessCategory', e.target.value)}
                label="Business Category *"
              >
                <MenuItem value="RETAIL">Retail</MenuItem>
                <MenuItem value="WHOLESALE">Wholesale</MenuItem>
                <MenuItem value="MANUFACTURING">Manufacturing</MenuItem>
                <MenuItem value="SERVICE">Service Provider</MenuItem>
                <MenuItem value="FOOD">Food & Beverage</MenuItem>
                <MenuItem value="ELECTRONICS">Electronics</MenuItem>
                <MenuItem value="CLOTHING">Clothing & Apparel</MenuItem>
                <MenuItem value="HEALTHCARE">Healthcare</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
              {errors.businessCategory && (
                <FormHelperText>{errors.businessCategory}</FormHelperText>
              )}
            </FormControl>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" gutterBottom>Directors Information</Typography>
            
            {formData.directors.map((director, index) => (
              <Paper key={index} sx={{ p: 3, position: 'relative', mb: 3 }}>
                <IconButton 
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                  onClick={() => removeDirector(index)}
                >
                  <Delete />
                </IconButton>

                <Typography variant="subtitle1">Director #{index + 1}</Typography>
                
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="First Name"
                    value={director.firstName}
                    onChange={(e) => handleFormChange(`directors.${index}.firstName`, e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                  <TextField
                    label="Last Name"
                    value={director.lastName}
                    onChange={(e) => handleFormChange(`directors.${index}.lastName`, e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="ID Number"
                    value={director.identification}
                    onChange={(e) => handleFormChange(`directors.${index}.identification`, e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>ID Type</InputLabel>
                    <Select
                      value={director.identificationType}
                      onChange={(e) => handleFormChange(`directors.${index}.identificationType`, e.target.value)}
                      label="ID Type"
                    >
                      <MenuItem value="IDENTITY CARD">National ID</MenuItem>
                      <MenuItem value="PASSPORT">Passport</MenuItem>
                      <MenuItem value="DRIVERS LICENSE">Driver's License</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="Email"
                    value={director.email}
                    onChange={(e) => handleFormChange(`directors.${index}.email`, e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                  <TextField
                    label="Phone"
                    value={director.telephone}
                    onChange={(e) => handleFormChange(`directors.${index}.telephone`, e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                </Box>
              </Paper>
            ))}

            <Button 
              variant="outlined" 
              startIcon={<Add />} 
              onClick={addDirector}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Director
            </Button>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" gutterBottom>Contact Information</Typography>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name *"
                value={formData.contactPerson.firstName}
                onChange={(e) => handleFormChange('contactPerson.firstName', e.target.value)}
                error={!!errors.contactPersonFirstName}
                helperText={errors.contactPersonFirstName}
                fullWidth
                variant="outlined"
              />
              <TextField
                label="Last Name"
                value={formData.contactPerson.lastName}
                onChange={(e) => handleFormChange('contactPerson.lastName', e.target.value)}
                fullWidth
                variant="outlined"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="ID Number"
                value={formData.contactPerson.identification}
                onChange={(e) => handleFormChange('contactPerson.identification', e.target.value)}
                fullWidth
                variant="outlined"
              />
              <FormControl fullWidth variant="outlined">
                <InputLabel>ID Type</InputLabel>
                <Select
                  value={formData.contactPerson.identificationType}
                  onChange={(e) => handleFormChange('contactPerson.identificationType', e.target.value)}
                  label="ID Type"
                >
                  <MenuItem value="IDENTITY CARD">National ID</MenuItem>
                  <MenuItem value="PASSPORT">Passport</MenuItem>
                  <MenuItem value="DRIVERS LICENSE">Driver's License</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Email Address *"
                type="email"
                value={formData.contactPerson.email}
                onChange={(e) => handleFormChange('contactPerson.email', e.target.value)}
                error={!!errors.contactPersonEmail}
                helperText={errors.contactPersonEmail}
                fullWidth
                variant="outlined"
              />
              <TextField
                label="Phone Number"
                value={formData.contactPerson.telephone}
                onChange={(e) => handleFormChange('contactPerson.telephone', e.target.value)}
                fullWidth
                variant="outlined"
              />
            </Box>

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>Business Address</Typography>
            
            <TextField
              label="Address Line 1 *"
              value={formData.communication.addressLine1}
              onChange={(e) => handleFormChange('communication.addressLine1', e.target.value)}
              error={!!errors.addressLine1}
              helperText={errors.addressLine1}
              fullWidth
              variant="outlined"
            />

            <TextField
              label="Address Line 2"
              value={formData.communication.addressLine2}
              onChange={(e) => handleFormChange('communication.addressLine2', e.target.value)}
              fullWidth
              variant="outlined"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="City"
                value={formData.communication.city}
                onChange={(e) => handleFormChange('communication.city', e.target.value)}
                fullWidth
                variant="outlined"
              />
              <TextField
                label="Region/Province"
                value={formData.communication.region}
                onChange={(e) => handleFormChange('communication.region', e.target.value)}
                fullWidth
                variant="outlined"
              />
              <TextField
                label="Postal Code"
                value={formData.communication.postalCode}
                onChange={(e) => handleFormChange('communication.postalCode', e.target.value)}
                fullWidth
                variant="outlined"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Country</InputLabel>
                <Select
                  value={formData.communication.country}
                  onChange={(e) => handleFormChange('communication.country', e.target.value)}
                  label="Country"
                >
                  <MenuItem value="ZIMBABWE">Zimbabwe</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Business Phone"
                value={formData.communication.telephone}
                onChange={(e) => handleFormChange('communication.telephone', e.target.value)}
                fullWidth
                variant="outlined"
              />
            </Box>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" gutterBottom>Outlets</Typography>
            
            {formData.outlets.map((outlet, index) => (
              <Paper key={index} sx={{ p: 3, position: 'relative', mb: 3 }}>
                <IconButton 
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                  onClick={() => removeOutlet(index)}
                >
                  <Delete />
                </IconButton>

                <Typography variant="subtitle1">Outlet #{index + 1}</Typography>
                
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="Outlet Name"
                    value={outlet.outletName}
                    onChange={(e) => handleFormChange(`outlets.${index}.outletName`, e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Outlet Type</InputLabel>
                    <Select
                      value={outlet.outletTypeId}
                      onChange={(e) => handleFormChange(`outlets.${index}.outletTypeId`, e.target.value)}
                      label="Outlet Type"
                    >
                      <MenuItem value="RETAIL">Retail Store</MenuItem>
                      <MenuItem value="WAREHOUSE">Warehouse</MenuItem>
                      <MenuItem value="SERVICE">Service Center</MenuItem>
                      <MenuItem value="RESTAURANT">Restaurant</MenuItem>
                      <MenuItem value="OFFICE">Office</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <TextField
                  label="Outlet Description"
                  value={outlet.outletDescription}
                  onChange={(e) => handleFormChange(`outlets.${index}.outletDescription`, e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mt: 2 }}
                  variant="outlined"
                />

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2">Contact Person</Typography>
                
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="First Name"
                    value={outlet.contactPerson.firstName}
                    onChange={(e) => handleFormChange(`outlets.${index}.contactPerson.firstName`, e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                  <TextField
                    label="Last Name"
                    value={outlet.contactPerson.lastName}
                    onChange={(e) => handleFormChange(`outlets.${index}.contactPerson.lastName`, e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="Email"
                    value={outlet.contactPerson.email}
                    onChange={(e) => handleFormChange(`outlets.${index}.contactPerson.email`, e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                  <TextField
                    label="Phone"
                    value={outlet.contactPerson.telephone}
                    onChange={(e) => handleFormChange(`outlets.${index}.contactPerson.telephone`, e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                </Box>

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2">Address</Typography>
                
                <TextField
                  label="Address Line 1"
                  value={outlet.communication.addressLine1}
                  onChange={(e) => handleFormChange(`outlets.${index}.communication.addressLine1`, e.target.value)}
                  fullWidth
                  sx={{ mt: 2 }}
                  variant="outlined"
                />

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="City"
                    value={outlet.communication.city}
                    onChange={(e) => handleFormChange(`outlets.${index}.communication.city`, e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                  <TextField
                    label="Region"
                    value={outlet.communication.region}
                    onChange={(e) => handleFormChange(`outlets.${index}.communication.region`, e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                  <TextField
                    label="Postal Code"
                    value={outlet.communication.postalCode}
                    onChange={(e) => handleFormChange(`outlets.${index}.communication.postalCode`, e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                </Box>

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2">Operating Hours</Typography>
                
                {Object.entries(outlet.operatingHours).map(([day, hours]) => (
                  <Box key={day} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ width: '100px' }}>
                      <Typography>{day.charAt(0).toUpperCase() + day.slice(1)}</Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={hours.closed}
                          onChange={(e) => handleFormChange(`outlets.${index}.operatingHours.${day}.closed`, e.target.checked)}
                        />
                      }
                      label="Closed"
                    />
                    <TextField
                      label="Open"
                      type="time"
                      value={hours.open}
                      onChange={(e) => handleFormChange(`outlets.${index}.operatingHours.${day}.open`, e.target.value)}
                      disabled={hours.closed}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      label="Close"
                      type="time"
                      value={hours.close}
                      onChange={(e) => handleFormChange(`outlets.${index}.operatingHours.${day}.close`, e.target.value)}
                      disabled={hours.closed}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      sx={{ width: 120 }}
                    />
                  </Box>
                ))}
              </Paper>
            ))}

            <Button 
              variant="outlined" 
              startIcon={<Add />} 
              onClick={addOutlet}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Outlet
            </Button>
          </Box>
        );

      case 4:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" gutterBottom>Banking Details</Typography>
            
            <TextField
              label="Bank Name *"
              value={formData.bankingDetails.bankName}
              onChange={(e) => handleFormChange('bankingDetails.bankName', e.target.value)}
              error={!!errors.bankName}
              helperText={errors.bankName}
              fullWidth
              variant="outlined"
            />

            <TextField
              label="Account Number *"
              value={formData.bankingDetails.accountNumber}
              onChange={(e) => handleFormChange('bankingDetails.accountNumber', e.target.value)}
              error={!!errors.accountNumber}
              helperText={errors.accountNumber}
              fullWidth
              variant="outlined"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Branch Code"
                value={formData.bankingDetails.branchCode}
                onChange={(e) => handleFormChange('bankingDetails.branchCode', e.target.value)}
                fullWidth
                variant="outlined"
              />
              <FormControl fullWidth variant="outlined">
                <InputLabel>Account Type</InputLabel>
                <Select
                  value={formData.bankingDetails.accountType}
                  onChange={(e) => handleFormChange('bankingDetails.accountType', e.target.value)}
                  label="Account Type"
                >
                  <MenuItem value="CURRENT">Current Account</MenuItem>
                  <MenuItem value="SAVINGS">Savings Account</MenuItem>
                  <MenuItem value="BUSINESS">Business Account</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TextField
              label="Account Holder Name"
              value={formData.bankingDetails.accountHolderName}
              onChange={(e) => handleFormChange('bankingDetails.accountHolderName', e.target.value)}
              fullWidth
              variant="outlined"
            />
          </Box>
        );

      case 5:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" gutterBottom>Document Upload</Typography>
            
            <Box sx={{ border: '1px dashed grey', p: 3, borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>Business Registration Document</Typography>
              <input
                type="file"
                id="registration-doc"
                style={{ display: 'none' }}
                onChange={(e) => handleFileUpload('registrationDoc', e.target.files)}
              />
              <label htmlFor="registration-doc">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<CloudUpload />}
                >
                  Upload
                </Button>
              </label>
              {formData.documents.registrationDoc && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {formData.documents.registrationDoc.name}
                  </Typography>
                  <IconButton size="small" onClick={() => removeFile('registrationDoc')}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>

            <Box sx={{ border: '1px dashed grey', p: 3, borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>Additional Documents (Optional)</Typography>
              <input
                type="file"
                id="other-docs"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleFileUpload('otherDocs', e.target.files)}
              />
              <label htmlFor="other-docs">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<CloudUpload />}
                >
                  Upload
                </Button>
              </label>
              {formData.documents.otherDocs.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {formData.documents.otherDocs.map((doc, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {doc.name}
                      </Typography>
                      <IconButton size="small" onClick={() => removeFile('otherDocs', index)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <FormControl error={!!errors.termsAgreed}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.termsAgreed}
                    onChange={(e) => handleFormChange('termsAgreed', e.target.checked)}
                  />
                }
                label="I agree to the Terms and Conditions *"
              />
              {errors.termsAgreed && (
                <FormHelperText>{errors.termsAgreed}</FormHelperText>
              )}
            </FormControl>
          </Box>
        );

      case 6:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              Registration submitted successfully!
            </Alert>
            <Typography variant="body1" paragraph>
              Thank you for registering as a vendor. Your application has been submitted and is pending review.
            </Typography>
            <Typography variant="body1" paragraph>
              You will receive an email notification once your application has been processed.
            </Typography>
            <Button variant="contained" href="/dashboard">
              Go to Dashboard
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {['Business Info', 'Directors', 'Contact & Address', 'Outlets', 'Banking Details', 'Documents & Agreement', 'Complete'].map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        {renderStepContent()}

        {activeStep < 6 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              variant="outlined"
            >
              Back
            </Button>

            <Button
              variant="contained"
              onClick={handleNext}
            >
              {activeStep === 5 ? 'Submit Application' : 'Continue'}
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default VendorOnboarding;